import React from "react";
import { Container, Typography, Box, Divider, Paper, List, ListItem, ListItemIcon, ListItemText, Alert } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLeaf, faTree, faWater, faFire, faGavel, faBuilding, faBookOpen, faExclamationTriangle, faUsers, faBolt, faRecycle } from "@fortawesome/free-solid-svg-icons";
import SectionTitle from "../../components/layouts/others/title-section.layout";

const POST20230915: React.FC = () => {
    return (
        <Container sx={{ mb: 5 }}>
            <SectionTitle title="ESPACE INFORMATIONS DES HABITANTS" />
            <Box mt={3}>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    align="center"
                    gutterBottom
                    color="primary"
                >
                    L'enjeu écologique
                </Typography>
            </Box>

            <Paper elevation={3} sx={{ p: 3, mt: 2, background: "#f5f9f6" }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <FontAwesomeIcon icon={faLeaf} color="#388e3c" size="lg" />
                    <Typography variant="h5" fontWeight={600} color="primary.dark">
                        Le contexte climatique
                    </Typography>
                </Box>
                <Typography gutterBottom>
                    La survenue d'événements climatiques inédits, de plus en plus fréquents
                    (canicule, sécheresse, inondations, <FontAwesomeIcon icon={faFire} color="#e57373"/> incendies, etc) n’est qu’un avant-goût des profonds changements climatiques en cours.
                    Un large consensus scientifique l’affirme.
                </Typography>
                <Typography gutterBottom>
                    <strong>Quels leviers pour Bois-le-Roi&nbsp;?</strong>&nbsp;Comment renforcer localement notre résilience et contribuer à la décarbonation&nbsp;?
                    Quelles actions concrètes pouvons-nous engager&nbsp;?
                </Typography>
                <Divider sx={{ mt: 2, mb: 2 }} />

                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <FontAwesomeIcon icon={faTree} color="#43a047" />
                    <Typography variant="subtitle1" fontWeight={600}>
                        Bois-le-Roi, un patrimoine naturel d’exception
                    </Typography>
                </Box>
                <Typography>
                    Entre Seine et forêt, la commune dispose d’une remarquable biodiversité.&nbsp;
                    <FontAwesomeIcon icon={faWater} color="#1976d2" /> Sources d’eau, trames écologiques, espaces boisés participent à un cadre de vie privilégié
                    et constituent <strong>un atout face au dérèglement climatique</strong>.
                </Typography>
                <Alert severity="success" sx={{ mt: 2 }}>
                    Préservons ensemble cet environnement pour les générations futures&nbsp;!
                </Alert>

                <Divider sx={{ mt: 3, mb: 2 }} />

                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <FontAwesomeIcon icon={faGavel} color="#556cd6" />
                    <Typography variant="subtitle1" fontWeight={600}>
                        Loi Climat et Résilience&nbsp;: discours et actes en décalage
                    </Typography>
                </Box>
                <Typography mb={1}>
                    Malgré la promesse de durabilité, des décisions contraires à l'esprit de la loi sont prises.
                </Typography>

                <List dense>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faTree} color="#795548" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Le Bois de la Source"
                            secondary={
                                <>
                                    <strong>Zéro Artificialisation Nette voté</strong>&nbsp;mais 1200m² boisés <strong>artificialisés</strong> pour bâtir une crèche,
                                    dans une zone hydrogéologique fragile&nbsp;: destruction de vieux chênes, perte de biodiversité et d’îlots de fraîcheur.
                                </>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faBookOpen} color="#1976d2" />
                        </ListItemIcon>
                        <ListItemText
                            primary="La médiathèque"
                            secondary={
                                <>
                                    Projet bâti selon l’ancienne norme <b>RT2012</b>, chauffage au gaz, coût record
                                    (<b>2,65&nbsp;M€ pour 450m²</b>), ignorée <b>RE2020</b> pourtant obligatoire&nbsp;:
                                    <span style={{ color: "#d84315", fontWeight: 600 }}> &nbsp;un impact écologique et économique contesté</span>.
                                </>
                            }
                        />
                    </ListItem>
                </List>
                <Divider sx={{ mt: 2, mb: 2 }} />

                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <FontAwesomeIcon icon={faBuilding} color="#607d8b" />
                    <Typography variant="subtitle1" fontWeight={600}>Le PLH&nbsp;: un projet opaque</Typography>
                </Box>
                <Typography gutterBottom>
                    <b>Absence de concertation</b>, zones constructibles décidées sans étude d’impact ni anticipation des besoins
                    en équipement, ni prise en compte des zones de biodiversité à sauvegarder.
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faUsers} color="#009688" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Mobilisation des habitants sous-estimée"
                            secondary="Des projets modifiés après protestation, mais toujours un manque de transparence et de concertation réelle."
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faLeaf} color="#43a047" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Conséquences environnementales ignorées"
                            secondary="Pas d’étude sur la biodiversité, zones à bâtir définies sans s’appuyer sur l’ABC de la biodiversité ni anticipation de la loi ZAN."
                        />
                    </ListItem>
                </List>
                <Divider sx={{ mt: 2, mb: 2 }} />

                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <FontAwesomeIcon icon={faBuilding} color="#c2185b" />
                    <Typography variant="subtitle1" fontWeight={600}>Le dossier Focel</Typography>
                </Box>
                <Typography>
                    Convention avec l'EPFIF&nbsp;: densification massive (≥ 100 logements sur 1,6ha),
                    <b>aucune garantie environnementale</b>&nbsp;(ZAN, trames vertes/bleues non respectées), impact sur réseaux non anticipé,
                    et perte de contrôle public.
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faBolt} color="#ffd600"/>
                        </ListItemIcon>
                        <ListItemText
                            primary="Surenchère urbaine"
                            secondary="Risque de ghettoïsation, infrastructures insuffisantes, mixité sociale menacée."
                        />
                    </ListItem>
                </List>
                <Divider sx={{ mt: 2, mb: 2 }} />

                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <FontAwesomeIcon icon={faRecycle} color="#43a047" />
                    <Typography variant="subtitle1" fontWeight={600}>Rénover plutôt que bétonner</Typography>
                </Box>
                <Typography>
                    L’enjeu écologique passe par la rénovation du bâti existant et l’usage optimal des logements vacants.&nbsp;
                    <b>Rien n’est engagé pour l’instant à Bois-le-Roi</b>.
                </Typography>
                <Divider sx={{ mt: 2, mb: 2 }} />
                <Alert icon={<FontAwesomeIcon icon={faExclamationTriangle} />} severity="warning" sx={{ fontWeight: 500, mb: 1 }}>
                    <b>Destruction d’un site naturel majeur, artificialisation des sols accrue, constructions dépassées, aucune politique de rénovation&nbsp;: l'enjeu écologique demeure ignoré dans la politique municipale.</b>
                </Alert>
            </Paper>
        </Container>
    );
};

export default POST20230915;